import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../common/axiosInstance';
import { DotLoader } from '../../components/loader/Loader';
import { AgGridReact } from "ag-grid-react";
import {
    ModuleRegistry,
    ClientSideRowModelModule,
    themeQuartz,
    ColDef,
    ICellRendererParams,
    GridReadyEvent,
    ValueGetterParams,
} from "ag-grid-community";
import images from "../../assets/images";
import { usePremisesStore } from "../../store/store";
import { Download } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const gridTheme = themeQuartz.withParams({
    backgroundColor: "#fff",
    foregroundColor: "#2a2a2a",
    headerTextColor: "#2a2a2a",
    headerBackgroundColor: "#E9F5FF",
    oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
    headerColumnResizeHandleColor: "rgb(126, 46, 132)",
    fontSize: "0.75rem",
    headerFontWeight: "bold",
});

interface ChecklistSummary {
    id: string;
    name?: string;
    premisesName?: string;
    premise?: string; // fallback
    status: string;
    date: string;
    createdAt?: string; // added if available
}

const SplitACChecklistList: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [checklists, setChecklists] = useState<ChecklistSummary[]>([]);
    const [error, setError] = useState<string | null>(null);
    const { allPremises, fetchPremises } = usePremisesStore();
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [customComment, setCustomComment] = useState('');

    useEffect(() => {
        fetchChecklists();
        fetchPremises();
    }, [fetchPremises]);

    const fetchChecklists = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axiosInstance.get('/api/split-ac-checklist');
            const data = response.data?.data || response.data || [];
            setChecklists(Array.isArray(data) ? data : []);
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.error || err.response?.data?.message || "Failed to fetch checklists");
            if (err.response?.status === 404 || err.response?.status === 500) {
                setChecklists([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this checklist?")) {
            return;
        }

        try {
            await axiosInstance.delete(`/api/split-ac-checklist/${id}`);
            await fetchChecklists();
        } catch (error: any) {
            console.error("Error deleting checklist:", error);
            alert(error.response?.data?.error || error.response?.data?.message || "Failed to delete checklist");
        }
    };

    const handleDownloadReport = async () => {
        if (!downloadingId) return;

        try {
            setLoading(true);
            const res = await axiosInstance.get(`/api/split-ac-checklist/${downloadingId}`);
            if (res.data?.data) {
                const checklist = res.data.data;
                const doc = new jsPDF();
                
                doc.setFontSize(18);
                doc.text("Split AC Maintenance Report", 14, 22);

                doc.setFontSize(10);
                const pId = checklist.premisesId || checklist.premise || '';
                const selectedPremise = allPremises.find((p: any) => p._id === pId);
                const premiseNameToUse = selectedPremise ? selectedPremise.premisesName : "N/A";

                doc.text(`Checklist Name: ${checklist.name || 'N/A'}`, 14, 32);
                doc.text(`Premise: ${premiseNameToUse}`, 14, 38);
                doc.text(`Date: ${checklist.date ? new Date(checklist.date).toLocaleDateString() : 'N/A'}`, 14, 44);

                if (customComment) {
                    doc.text(`Comments: ${customComment}`, 14, 50);
                }

                const tableColumn = ["Sr. No.", "Particulars", "Status", "Remarks"];
                const tableRows = (checklist.items || []).map((item: any) => [
                    (item.srNo ?? '').toString(),
                    item.particulars || '',
                    item.status || "N/A",
                    item.remarks || "N/A"
                ]);

                autoTable(doc, {
                    head: [tableColumn],
                    body: tableRows,
                    startY: customComment ? 58 : 52,
                    styles: { fontSize: 8, cellPadding: 3 },
                    headStyles: { fillColor: [0, 168, 165] },
                    columnStyles: {
                        0: { cellWidth: 15 },
                        1: { cellWidth: 80 },
                        2: { cellWidth: 30 },
                        3: { cellWidth: 'auto' }
                    }
                });

                doc.save(`Split_AC_Report_${checklist.name || 'Checklist'}.pdf`);
            }
        } catch (err: any) {
            console.error("Error downloading PDF:", err);
            alert("Failed to download PDF report");
        } finally {
            setLoading(false);
            setShowDownloadModal(false);
            setCustomComment('');
            setDownloadingId(null);
        }
    };

    const formatDateOnly = (dateString: string) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        } catch {
            return "N/A";
        }
    };

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                field: "name",
                headerName: "Checklist Name",
                sortable: true,
                flex: 2,
                minWidth: 200,
                headerStyle: { backgroundColor: "#e9f5ff" } as any,
                cellStyle: {
                    padding: "12px 8px",
                    textAlign: "left",
                } as any,
                cellRenderer: (params: ICellRendererParams) => (
                    <div className="truncate" title={params.value || "N/A"}>
                        {params.value || "N/A"}
                    </div>
                ),
            },
            {
                field: "premisesName",
                headerName: "Premises",
                sortable: true,
                flex: 1.5,
                minWidth: 150,
                headerStyle: { backgroundColor: "#e9f5ff" } as any,
                cellStyle: {
                    padding: "12px 8px",
                    textAlign: "left",
                } as any,
                valueGetter: (params: ValueGetterParams) => {
                    if (params.data.premisesName) {
                        return params.data.premisesName;
                    }
                    if (params.data.premise) {
                        const matched = allPremises.find((p: any) => p._id === params.data.premise);
                        return matched ? matched.premisesName : params.data.premise;
                    }
                    return '';
                },
                cellRenderer: (params: ICellRendererParams) => (
                    <div className="truncate" title={params.value || "N/A"}>
                        {params.value ? (
                            <span>{params.value}</span>
                        ) : (
                            <span className="text-gray-400">N/A</span>
                        )}
                    </div>
                ),
            },
            {
                field: "date",
                headerName: "Date",
                sortable: true,
                flex: 1,
                minWidth: 120,
                headerStyle: { backgroundColor: "#e9f5ff", textAlign: "left" } as any,
                cellStyle: {
                    padding: "12px 8px",
                    textAlign: "left",
                } as any,
                cellRenderer: (params: ICellRendererParams) => (
                    <div>
                        {params.value ? (
                            <span>{formatDateOnly(params.value)}</span>
                        ) : (
                            <span className="text-gray-400">N/A</span>
                        )}
                    </div>
                ),
            },
            {
                field: "status",
                headerName: "Status",
                sortable: true,
                flex: 1,
                minWidth: 120,
                headerStyle: { backgroundColor: "#e9f5ff", textAlign: "left" } as any,
                cellStyle: {
                    padding: "12px 8px",
                    textAlign: "left",
                } as any,
                cellRenderer: (params: ICellRendererParams) => {
                    const status = params.value || 'Submitted';
                    return (
                        <span className={`px-2 py-1 rounded text-xs px-3 font-semibold ${status === 'submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {status}
                        </span>
                    );
                },
            },
            {
                field: "action",
                headerName: "Actions",
                sortable: false,
                flex: 1.2,
                minWidth: 150,
                headerStyle: { backgroundColor: "#e9f5ff", textAlign: "left" } as any,
                cellStyle: {
                    padding: "12px 8px",
                    textAlign: "left",
                } as any,
                cellRenderer: (params: ICellRendererParams) => (
                    <div className="flex gap-x-3 justify-start items-center">
                        <button
                            onClick={() => {
                                setDownloadingId(params.data.id);
                                setShowDownloadModal(true);
                            }}
                            className="p-1 hover:bg-teal-50 rounded transition-colors text-[#00A8A5]"
                            title="Download PDF"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate(`/safety-equipment/split-ac-checklist/${params.data.id}`)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title="View/Edit"
                        >
                            <img className="w-5 h-5" src={images.viewIcon} alt="View" />
                        </button>
                        <button
                            onClick={() => handleDelete(params.data.id)}
                            className="p-1 hover:bg-red-50 rounded transition-colors text-red-600"
                            title="Delete"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                ),
            },
        ],
        [navigate, allPremises]
    );

    const defaultColDef = useMemo(
        () => ({
            sortable: true,
        }),
        []
    );

    const onGridReady = useCallback((e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <DotLoader />
            </div>
        );
    }

    return (
        <div className="w-full px-6 py-4">
            <div className="w-full mt-20 mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            Split AC Checklist Management
                        </h2>
                        <p className="text-gray-600">
                            View, create, and manage your Split AC checklists
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate("/safety-equipment")}
                            className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <button
                            onClick={() => navigate("/safety-equipment/split-ac-checklist/new")}
                            className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors shadow-lg hover:shadow-xl"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create New Checklist
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-lg shadow-lg p-6">
                {checklists.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">
                            No Checklists Found
                        </h3>
                        <p className="text-gray-500 mb-6">
                            Get started by creating your first Split AC checklist
                        </p>
                        <button
                            onClick={() => navigate("/safety-equipment/split-ac-checklist/new")}
                            className="inline-flex items-center gap-2 py-2 px-6 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create Your First Checklist
                        </button>
                    </div>
                ) : (
                    <div
                        className="ag-theme-quartz w-full shadow-lg rounded-lg overflow-hidden"
                        style={{ height: "calc(100vh - 280px)" }}
                    >
                        <AgGridReact
                            theme={gridTheme}
                            rowData={checklists}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            rowHeight={48}
                            headerHeight={48}
                            animateRows={true}
                            onGridReady={onGridReady}
                            rowSelection="multiple"
                            suppressRowClickSelection={true}
                            pagination={true}
                            paginationPageSize={10}
                            paginationPageSizeSelector={[10, 20, 50, 100]}
                        />
                    </div>
                )}
            </div>

            {showDownloadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Download Report</h3>
                        <p className="text-sm text-gray-600 mb-4">Add an optional custom comment to include in the PDF report.</p>
                        <textarea
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A8A5] mb-6 min-h-[100px]"
                            placeholder="Enter your comment here..."
                            value={customComment}
                            onChange={(e) => setCustomComment(e.target.value)}
                        />
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowDownloadModal(false);
                                    setCustomComment('');
                                    setDownloadingId(null);
                                }}
                                className="px-4 py-2 rounded text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDownloadReport}
                                className="px-4 py-2 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors flex items-center gap-2"
                            >
                                <Download size={16} />
                                Download PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SplitACChecklistList;
